<template>
  <div class="flex flex-col flex-grow">
    <div
      v-if="editor"
      :class="[
        vuelidateErrors.length || hasError
          ? 'rounded border text-red-900 border-red-300 focus:border-red-500 focus:ring-red-500'
          : '',
        showIcon ? 'pr-10' : '',
        editable ? 'editor  border border-solid border-gray-300 rounded shadow-sm' : '',
      ]"
      class="flex flex-col text-black bg-white"
    >
      <MenuBar
        v-if="displayHeader"
        class="editor__header flex items-center flex-wrap"
        :editor="editor"
        :editable="editable"
      />
      <EditorContent
        class="editor__content !text-indigo-base !font-montserrat flex-auto"
        :class="{ 'editor__content--resizable': isResizable }"
        :editor="editor"
      />
      <AlertCircleIcon
        v-if="showIcon && vuelidateErrors.length"
        class="absolute right-1.5 bottom-1"
        color="red"
        size="24"
      />
    </div>
    <div class="flex justify-between">
      <div v-if="editable" class="text-right text-sm order-2 text-gray-500 ml-auto">
        <p v-if="editor">
          {{ characterCount }}<span v-if="showLimit">/{{ characterLimit }}</span>
        </p>
      </div>
      <div v-if="vuelidateErrors.length">
        <div v-for="error of vuelidateErrors" :key="error.$uid">
          <p class="text-sm text-red-600">{{ error.$message }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CharacterCount from "@tiptap/extension-character-count";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import { Editor, EditorContent } from "@tiptap/vue-3";
import MenuBar from "@/components/forms/SharedComponents/WYSIWYG/MenuBar.vue";
import AlertCircleIcon from "@/components/shared/icons/AlertCircleIcon.vue";

export default {
  components: {
    EditorContent,
    MenuBar,
    AlertCircleIcon,
  },
  props: {
    editable: {
      type: Boolean,
      default: true,
    },
    resizable: {
      type: Boolean,
      default: false,
    },
    content: {
      type: Object,
      default() {
        return {};
      },
    },

    modelValue: {
      type: [Object, String, Number],
      default() {
        return {};
      },
    },
    vuelidateErrors: {
      type: Array,
      default: () => [],
    },
    characterLimit: {
      type: Number,
      default: 500,
    },
    showLimit: {
      type: Boolean,
      default: false,
    },
    displayHeader: {
      type: Boolean,
      default: true,
    },
    showErrorIcon: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["updateContent", "update:modelValue", "characterCount"],
  data() {
    return {
      editor: null,
      isResizable: this.resizable,
    };
  },
  computed: {
    jsonContent() {
      return this.editor ? this.editor.getJSON() : {};
    },
    showIcon() {
      return this.showErrorIcon && this.vuelidateErrors.length;
    },
    characterCount() {
      return this.editor?.storage?.characterCount?.characters() ?? 0;
    },
  },
  watch: {
    jsonContent: {
      handler: function (val) {
        /*
          *****IMPORTANT***** Create updateContent method on the parent component.
          This will return a JSON with the content, will be triggered each time the content changes
        */
        this.$emit("updateContent", val);
        this.$emit("update:modelValue", val);
      },
    },
    editable: {
      handler: function (val) {
        this.editor.setEditable(val);
      },
    },
    content: {
      handler: function (val) {
        if (JSON.stringify(this.editor.getJSON()) === JSON.stringify(val)) return;
        this.editor.commands.setContent(val);
      },
    },
  },
  mounted() {
    this.editor = new Editor({
      editable: this.editable,
      content: !Object.entries(this.content).length ? "" : this.content,
      extensions: [
        StarterKit,
        Underline,
        Link.configure({
          openOnClick: false,
        }),
        CharacterCount.configure({
          limit: this.characterLimit + 1,
        }),
      ],
      onUpdate: ({ editor }) => {
        this.$emit("characterCount", editor.storage.characterCount.characters());
      },
    });
    if (this.modelValue && Object.hasOwn(this.modelValue, "content")) {
      this.editor.commands.setContent(this.modelValue.content);
    }
    this.$emit("characterCount", this.editor.storage.characterCount.characters());
  },
  beforeUnmount() {
    this.editor.destroy();
  },
};
</script>

<style>
.editor {
  max-height: 26rem;
}
.editor__header {
  flex: 0 0 auto;
  padding: 0.55rem;
  border-bottom: 1px solid #e5e7e8;
}
.editor__content--resizable {
  resize: vertical;
}
.editor__content {
  padding: 1.25rem 1rem;
  overflow-x: hidden;
  overflow-y: auto;
  height: 9rem;
  -webkit-overflow-scrolling: touch;
}

.ProseMirror-focused:focus,
.ProseMirror:focus {
  outline: none !important;
}
/* Styles for mobile devices */
@media (max-width: 768px) {
  .ProseMirror ul li {
    position: relative;
    padding-left: 1rem;
    margin-bottom: 1rem; /* Adjust the spacing between each list item at the bottom */
  }
  .ProseMirror ul li::before {
    content: "\2022"; /* Use the Unicode character for a bullet point (disc) */
    position: absolute;
    left: 0;
  }
  .ProseMirror ol li {
    position: relative;
    padding-left: 1rem;
    margin-bottom: 1rem;
  }
  .ProseMirror ol li::before {
    content: counters(list-item, ".") ".";
    counter-increment: list-item;
    position: absolute;
    left: 0;
  }
}

/* Styles for desktop devices */
@media (min-width: 769px) {
  .ProseMirror ul {
    padding: 0 1rem;
    list-style: disc;
  }
  .ProseMirror ol {
    padding: 0 1rem;
    list-style: decimal;
  }
}

.ProseMirror a {
  font-weight: 600;
  text-decoration: underline;
  color: #007f80;
}
</style>
